import { normalizePath, useSetFlowTo } from 'Simple/Flow'
import { useMutation } from 'Data/Api'
import { useDataValue, useDataSubmit } from 'Simple/Data'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import mutation from './mutation.graphql.js'

// TODO: this likely needs to be mapped to the new type/fields because
// I think it references an app automation's event not the actual event?
function validate(value) {
  return value
    .filter(step => step.step_type === 'automation')
    .map(({ event }) => {
      if (!event?.data) {
        return null
      }
      switch (event.type) {
        case 'txs_status_update': {
          if (!event.data.value_to?.length) {
            return 'Please select treatment status'
          }

          break
        }
        case 'appointment_bookings_state_update': {
          if (
            !event.data.value_to?.length ||
            !event.data.appointment_type_ids?.length
          ) {
            return 'Please select appointment types and status'
          }

          break
        }
        case 'custom_resource_insert': {
          if (!event.data.custom_resource_type) {
            return 'Please select the custom resource type'
          }
          break
        }

        default:
          return null
      }

      return null
    })
    .filter(Boolean)
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location._id',
  })
  let appName = useDataValue({
    viewPath: props.viewPath,
    context: 'app',
    path: 'name',
  })
  let app_id = useDataValue({
    viewPath: props.viewPath,
    context: 'app',
    path: 'id',
  })

  let submitAutomations = useDataSubmit({
    context: 'automations',
    viewPath: props.viewPath,
  })

  let [, executeMutation] = useMutation(mutation)
  let setFlow = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args.type) {
      case 'changeStatus': {
        let isLast = value.current_index === value.steps.length - 1

        if (isLast) {
          await submit(
            value.steps.map((step, index) => {
              if (index === value.current_index) {
                return {
                  ...step,
                  status: args.value,
                }
              }

              return step
            })
          )

          submitAutomations({ type: 'refetch' })
          setFlow(normalizePath(props.viewPath, '../../No'))
        } else {
          change(next => {
            if (next.steps[next.current_index]) {
              next.steps[next.current_index].status = args.value
              next.current_index = next.current_index + 1
            }
          })
        }

        return
      }
      default: {
        await submit(
          value.steps.map((step, index) => {
            if (index === value.current_index) {
              return {
                ...step,
                status: 'completed',
              }
            }

            return step
          })
        )
        submitAutomations({ type: 'refetch' })
        setFlow(normalizePath(props.viewPath, '../../No'))

        return
      }
    }
  }

  async function submit(steps) {
    let errorMessages = validate(steps)

    if (errorMessages?.length) {
      return notify(notifyError(errorMessages.join('\n')))
    }

    let automationSteps =
      steps.filter(
        item => item.step_type === 'automation' && item.status === 'completed'
      ) || []

    if (automationSteps.length > 0) {
      let mutationResponse = await executeMutation({
        location_id,
        events: automationSteps.map(
          ({ event, automation_id, required, name }) => ({
            ...event,
            name,
            automation_id,
            required,
          })
        ),
        app_id,
      })

      if (mutationResponse.error) {
        notify(
          notifyError(
            'Something went wrong. Please, try again or contact support if the problem persists.'
          )
        )

        return
      } else {
        notify(
          notifySuccess(`Setup automations for "${appName}" successfully.`)
        )
      }
    }
  }
}
